import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import {
  ERequestState,
  FileDownload,
  MembershipRequestApi,
  MembershipRequestGridDto,
  MembershipRequestSearch,
} from "../../../../api";
import { AddButton, CloseButton, EditButton, SearchTablePage, ViewButton } from "../../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { b64toBlob } from "../../../../utils";
import { saveAs } from "file-saver";

export interface IMembershipRequestListPageProps {}

export const MembershipRequestListPage: React.FunctionComponent<IMembershipRequestListPageProps> = (props) => {
  const api = useApiService(MembershipRequestApi);
  const { dispatchEvent } = useEventsContext();
  const [selectAll, setselectAll] = React.useState(false);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const [billedExportLoading, setbilledExportLoading] = React.useState(false);
  const BilledExport = React.useCallback(async () => {
    setbilledExportLoading(true);
    try {
      var file = await api.billedMembershipReqhestsExport();
      if (file?.fileBytes) {
        var blob = b64toBlob(new FileDownload({ base64Content: file.fileBytes, fileName: file.fileName }));
        saveAs(blob, file.fileName);
      } else {
        showError(t(ETLCodes.Error));
      }
    } catch {
      showError(t(ETLCodes.Error));
    }
    setbilledExportLoading(false);
  }, [api, t]);
  const [paidExportLoading, setpaidExportLoading] = React.useState(false);
  const PaidExport = React.useCallback(async () => {
    setpaidExportLoading(true);
    try {
      var file = await api.executedMembershipReqhestsExport();
      if (file?.fileBytes) {
        var blob = b64toBlob(new FileDownload({ base64Content: file.fileBytes, fileName: file.fileName }));
        saveAs(blob, file.fileName);
      } else {
        showError(t(ETLCodes.Error));
      }
    } catch {
      showError(t(ETLCodes.Error));
    }
    setpaidExportLoading(false);
  }, [api, t]);

  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const { showDialog } = useDialog();
  const columnCheckBox = React.useCallback(
    (row: MembershipRequestGridDto) => {
      const disabled = row.requestStateCode !== ERequestState.PAID;
      if (!disabled) {
        closableIdsList.push(row.requestId);
      }
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
          disabled={disabled}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: MembershipRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.membershipRequest}/${row.membershipRequestId}/detail/view`)}
            />
            <EditButton
              minimal={true}
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              onClick={() => history.push(`${ERoutes.membershipRequest}/${row.membershipRequestId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: MembershipRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: MembershipRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.MembershipRequestType),
        fieldName: "membershipRequestType",
      },
      {
        header: () => t(ETLCodes.MembershipType),
        fieldName: "membershipType",
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [columnCheckBox, history, t, toggleSelectAll]
  );

  const closeRequests = React.useCallback(
    (requestList) => {
      if (selectionList.length === 0) {
        showDialog({
          message: t(ETLCodes.CloseRequestSelectionEmpty),
          title: t(ETLCodes.CloseRequests),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseRequestConfirmationMessage),
          title: t(ETLCodes.CloseRequests),
          onConfirmed: async () => {
            try {
              requestList.forEach(async (element, index) => {
                api.close(element).then(() => dispatchEvent("RefreshSearchTables"));
              });
              showSuccess(t(ETLCodes.RequestsClosedSuccess));
            } catch (error) {
              showError(t(ETLCodes.Error));
            }
          },
        });
      }
    },
    [api, dispatchEvent, selectionList.length, showDialog, t]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.membershipRequest}/0/detail/edit`);
  }, [history]);

  const search = React.useCallback(
    (sObj) =>
      api.baseSearch(
        MembershipRequestSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    []
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(
          MembershipRequestSearch.fromJS({
            ...sObj,
            languageId: currentLanguage.languageId,
          })
        )
      }
      sortKeys={{ requestDate: "DESC", requestNumber: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.MembershipRequests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => addItemFunction()} />
          <CloseButton text={t(ETLCodes.Close)} onClick={() => closeRequests(selectionList)} />
          <Button
            text={t(ETLCodes.BilledExport)}
            icon={<i className="far fa-file-excel" />}
            onClick={() => BilledExport()}
            loading={billedExportLoading}
            minimal
            intent={Intent.PRIMARY}
          />
          <Button
            text={t(ETLCodes.PaidExport)}
            icon={<i className="far fa-file-excel" />}
            onClick={() => PaidExport()}
            loading={paidExportLoading}
            minimal
            intent={Intent.PRIMARY}
          />
        </div>
      }
    />
  );
};
